import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../../services/services';
import Field from '../../../../interfaces/field';
import Table from '../../../../components/common/table/table';
import css from './index.module.css';
import { Option } from '../../../../components/form/elements/select';
import { ListParams } from '../../../../services/http-client';
import { faChevronDown, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { can, route, uniqueValues } from '../../../../services/utils';
import {
  CROP_FIELD_VIEW,
  CROP_FIELDS_AND_WORKS_INDEX,
} from '../../../../router/routes';
import TabsKeys from '../../../../enums/tab-keys';
import { RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import ShowIfAllowed from '../../../../components/common/show-if-allowed';
import { useBackButton } from '../backButtonContext';
import { Section } from '../index';
import { useQuery } from '../../../../hooks/use-query';
import { setFieldsFilter } from '../../../../store/global-slice';
import { useSelectedRows } from '../../../../components/common/table/selected-rows-context';
import useColumn from '../../../../components/common/table/use-column';
import TableConfigurationModal from '../../../../components/common/table/table-configuration-modal/table-configuration-modal';
import CreateNewFieldModal from './components/create-new-field-modal';
import TableVerticalSizeFixer from '../../../../components/common/table/table-vertical-size-fixer/table-vertical-size-fixer';
import ShowFilter from '../../../../components/common/table/show-filter/show-filter';
import ExcelButtonNew from '../../../../components/common/table/excel-button/excel-button-new';
import ValueFilter from '../../../../components/common/table/value-filter/value-filter';
import SettingButton from '../../../../components/common/table/setting-button/setting-button';
import { ButtonComponent } from '../../../../components/form/elements/button';
import ActionButtons from './components/action-buttons';

const tableConfigurationConfig = 'FIELDS';
const areaFixedAmount = 4;

export default function Fields({
  setShowFilter,
  showFilter,
}: {
  setShowFilter: Dispatch<SetStateAction<boolean>>;
  showFilter: boolean;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { fieldService, workService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const [cropValuesFilterItems, setCropValuesFilterItems] = useState([]);
  const [cropGroupsFilterItems, setCropGroupsFilterItems] = useState([]);
  const [ladNumbersFilterItems, setLadNumbersFilterItems] = useState([]);
  const [blockNumbersFilterItems, setBlockNumbersFilterItems] = useState([]);
  const [nameFilterItems, setNameFilterItems] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showTableConfigurationModal, setShowTableConfigurationModal] =
    useState(false);
  const [showCreateNewField, setShowCreateNewField] = useState(false);
  const [selectedFields, setSelectedFields] = useSelectedRows<Field>();

  const { setBackButton } = useBackButton();
  const [params] = useQuery();
  const dispatch = useDispatch();
  const transKeyPrefix = 'pages.crop.fields_and_works.fields.';
  const tp = useCallback(
    (key: string, params?: { [key: string]: string | number }) =>
      t(transKeyPrefix.concat(key), params),
    [t]
  );
  const { columns } = useColumn({ config: tableConfigurationConfig });

  const { farmUser, fieldsFilter } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
    farm: state.global.farm,
    fieldsFilter: state.global.fieldsFilter,
  }));

  useEffect(() => {
    if (!isInitialized) {
      if (fieldsFilter) {
        history.push({ ...history.location, search: fieldsFilter });
      } else {
        history.push({ ...history.location, search: '?active=yes' });
      }

      setIsInitialized(true);
    } else {
      if (fieldsFilter !== history.location.search) {
        dispatch(setFieldsFilter(history.location.search));
      }
    }
  }, [
    dispatch,
    fieldsFilter,
    history,
    history.location,
    isInitialized,
    params,
  ]);

  useEffect(() => {
    setBackButton(
      route(CROP_FIELDS_AND_WORKS_INDEX, {
        farmId: farmId,
        season: season,
        tab: Section.fields,
      })
    );
  }, [farmId, season, setBackButton]);

  useEffect(() => {
    const subscription = fieldService
      .getCropGroupFilterValues(farmId, season)
      .subscribe((values: any[]) => {
        let cropGroups: Option[] = [];
        let cropValues: Option[] = [];

        Object.values(values).forEach((value) => {
          cropValues.push(value as Option);
          cropGroups.push(value.group as Option);
        });

        cropGroups = uniqueValues(cropGroups, 'label');

        cropGroups.sort((a, b) => a.label.localeCompare(b.label));

        setCropValuesFilterItems(cropValues);
        setCropGroupsFilterItems(cropGroups);
      });

    return () => subscription.unsubscribe();
  }, [
    fieldService,
    farmId,
    season,
    setCropValuesFilterItems,
    setCropGroupsFilterItems,
  ]);

  useEffect(() => {
    const subscription = fieldService
      .getLadNumbersFilterValues(farmId, season)
      .subscribe((values: Option[]) => {
        const arrayUniqueByKey = [
          ...new Map(values.map((item) => [item['value'], item])).values(),
        ];

        setLadNumbersFilterItems(arrayUniqueByKey);
      });
    return () => subscription.unsubscribe();
  }, [fieldService, farmId, season]);

  useEffect(() => {
    const subscription = fieldService
      .getBlockNumbersFilterValues(farmId, season)
      .subscribe((values: Option[]) => {
        values.sort((a, b) => a.label.localeCompare(b.label));

        setBlockNumbersFilterItems(values);
      });
    return () => subscription.unsubscribe();
  }, [fieldService, farmId, season]);

  useEffect(() => {
    const subscription = fieldService
      .getNameFilterValues(farmId, season)
      .subscribe((values: Option[]) => {
        values.sort((a, b) => a.label.localeCompare(b.label));

        setNameFilterItems(values);
      });
    return () => subscription.unsubscribe();
  }, [fieldService, farmId, season]);

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      fieldService.index(farmId, season, { page, sort, desc, ...filter }, true),
    [fieldService, farmId, season]
  );

  const renderTableArea = (data) => {
    const innerData = selectedFields?.length ? selectedFields : data;

    return innerData
      ?.reduce(function (a, b) {
        return a + b['area'];
      }, 0)
      ?.toFixed(areaFixedAmount);
  };

  const toolbar = useCallback(
    (params: ListParams): JSX.Element => {
      return (
        <>
          <div className={css['topbar']}>
            <ShowIfAllowed ability={{ title: 'create work' }}>
              <ActionButtons
                enabledButtons={workService.allowedWorkTypesForFields(
                  selectedFields
                )}
                fields={selectedFields.map((field: Field) => field.id)}
              />
            </ShowIfAllowed>
            <ShowIfAllowed
              ability={{ title: 'create field' }}
              key="show-if-allowed-add"
            >
              <ButtonComponent
                name="add"
                key="add"
                label={transKeyPrefix + 'add_new_field'}
                click={() => setShowCreateNewField(true)}
                icon={faPlus}
              />
            </ShowIfAllowed>
          </div>
          <div className={css['show_filter_div']}>
            <div className={css['filter-show']}>
              <label htmlFor="show-filter"></label>
              <ShowFilter
                onClick={() => setShowFilter(!showFilter)}
                defaultActiveValue={true}
              />
            </div>
            <div className={css['excel-export']}>
              <label htmlFor="excel-export"></label>
              <SettingButton
                onClick={() => setShowTableConfigurationModal(true)}
              />
            </div>
            <div className={css['excel-export']}>
              <label htmlFor="excel-export"></label>
              <ExcelButtonNew
                onClick={() => {
                  const filter = { ...params.params };

                  delete params.params;
                  return fieldService
                    .exportXls(farmId, season, { ...filter, ...params })
                    .subscribe();
                }}
              />
            </div>
          </div>
          {showFilter && (
            <div className={css['filter_body_div']}>
              <div className={css['filter_div']}>
                <label className={css['filter-label']}>
                  {t('pages.crop.fields_and_works.fields.lad_number')}
                </label>
                <ValueFilter
                  name="lad_number"
                  className={'value-filter-width-160'}
                  options={ladNumbersFilterItems}
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                  enableClearOnEmpty={true}
                />
              </div>
              <div className={css['filter_div']}>
                <label className={css['filter-label']}>
                  {t('pages.crop.fields_and_works.fields.block_number')}
                </label>
                <ValueFilter
                  name="block_number"
                  className={'value-filter-width-190'}
                  options={blockNumbersFilterItems}
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                  enableClearOnEmpty={true}
                />
              </div>
              <div className={css['filter_div']}>
                <label className={css['filter-label']}>
                  {t('pages.crop.fields_and_works.fields.name')}
                </label>
                <ValueFilter
                  name="name"
                  className={'value-filter-width-160'}
                  options={nameFilterItems}
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                  enableClearOnEmpty={true}
                />
              </div>
              <div className={css['filter_div']}>
                <label className={css['filter-label']}>
                  {t('pages.crop.fields_and_works.fields.crop_group')}
                </label>
                <ValueFilter
                  name="crop_group_id"
                  className={'value-filter-width-160'}
                  options={cropGroupsFilterItems}
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                  enableClearOnEmpty={true}
                  disableAutoSelect={true}
                />
              </div>
              <div className={css['filter_div']}>
                <label className={css['filter-label']}>
                  {t('pages.crop.fields_and_works.fields.crop')}
                </label>
                <ValueFilter
                  name="crop_id"
                  className={'value-filter-width-190'}
                  options={cropValuesFilterItems}
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                  enableClearOnEmpty={true}
                  disableAutoSelect={true}
                />
              </div>
              <div className={css['filter_div']}>
                <label className={css['filter-label']}>
                  {t('pages.crop.fields_and_works.fields.active')}
                </label>
                <ValueFilter
                  name="active"
                  className={'value-filter-width-160'}
                  options={[
                    {
                      label: t('pages.crop.fields_and_works.fields.active'),
                      value: 'yes',
                    },
                    {
                      label: t('pages.crop.fields_and_works.fields.inactive'),
                      value: 'no',
                    },
                  ]}
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                />
              </div>
            </div>
          )}
        </>
      );
    },
    [
      farmId,
      season,
      fieldService,
      showFilter,
      ladNumbersFilterItems,
      blockNumbersFilterItems,
      nameFilterItems,
      cropGroupsFilterItems,
      cropValuesFilterItems,
      setShowFilter,
      selectedFields,
      workService,
      t,
    ]
  );

  return !isInitialized ? (
    <></>
  ) : (
    <div className={css['body']}>
      <TableVerticalSizeFixer>
        <Table<Field>
          toolbar={toolbar}
          request={request}
          paginate={false}
          keyAccessor="id"
          columns={columns}
          viewRoute={route(CROP_FIELD_VIEW, {
            farmId: farmId,
            tab: TabsKeys.basic_info,
            season: season,
          })}
          viewRecordAbility={{ title: 'view field' }}
          selectedRowsState={
            can(farmUser, { title: 'merge field' }) ||
            can(farmUser, { title: 'split field' }) ||
            can(farmUser, { title: 'create work' })
              ? [selectedFields, setSelectedFields]
              : null
          }
          filteredTotalsParams={{
            recordName: 'table_title',
            fullText: {
              tp,
              key: selectedFields?.length
                ? 'table_title_selected'
                : 'table_title',
              keys: [
                {
                  key: 'area',
                  value: (data) => renderTableArea(data),
                },
                {
                  key: 'selected',
                  value: () => selectedFields?.length,
                },
              ],
            },
          }}
        />
      </TableVerticalSizeFixer>
      {showTableConfigurationModal && (
        <TableConfigurationModal
          config={tableConfigurationConfig}
          setShowTableConfigurationModal={setShowTableConfigurationModal}
        />
      )}
      {showCreateNewField && (
        <CreateNewFieldModal setShowModal={setShowCreateNewField} />
      )}
    </div>
  );
}
